/**
 * Scope-doc harvest: turns an EDITED Scope doc (Tiptap JSON) back into a model diff for
 * PUT /sales/deals/:dealUuid/questionnaire-document. Baseline-diff by design: the model AS THE
 * USER SAW IT when editing began (the `baseline` snapshot; falls back to the current model) is
 * re-projected through the SAME builder (buildScopeDoc, edit mode) and both docs are parsed
 * with the SAME rules - so an untouched field can never emit a write, and a concurrent change
 * (AI fill, split-pane edit, another user's save) to a field the user did NOT touch is never
 * reverted by the save.
 *
 * What harvests (anchored by `dataKey` attrs - headings AND rows, see scope-doc.ts):
 * - `field:` anchors with a SCOPE_DOC_TEXT_CONTROLS control: body text -> answer upsert.
 * - `adhoc:` anchors: discussion text -> answer; `brief:<dim>` rows -> full brief-subset
 *   replace; containing ad-hoc section -> Business-Process move; doc position -> item order.
 * - dataKey-null H2/H3 headings inside an ad-hoc section -> NEW scope items (client-generated
 *   fieldKey so a retried save stays idempotent server-side).
 * What deliberately does NOT harvest: `chips:*` rows (status/work-type/hours, enum answers) and
 * tables - those stay owned by the question view / Prioritization pane in this increment. Text
 * typed where it cannot be saved is returned in `discarded` so the caller can refuse the save
 * instead of silently dropping it (confirm-or-override).
 */

import {
  ADHOC_SECTION_KEY,
  isAdhocGroupSectionKey,
  isAdhocSectionKey,
  parseHumanCurrency,
  SCOPE_BRIEF,
  type ScopeMeta,
} from '@protoss/shared';

import type {
  SalesDealQuestionnaireSummary,
  SalesDealTemplateFieldSummary,
} from '../services/salesApi';
import { buildScopeDoc, parseScopeDocKey, SCOPE_DOC_TEXT_CONTROLS } from './scope-doc';
import type {
  QuestionnaireAnswerMap,
  QuestionnaireFieldLookup,
} from './questionnaire-field-visibility';

// ── Payload (mirrors ApplyScopeDocumentBody server-side) ─────────────────────

export interface ScopeDocumentPayload {
  answers: Array<{ templateFieldId: number | null; fieldKey: string; value: unknown }>;
  briefEdits: Array<{ fieldKey: string; scopeMeta: ScopeMeta }>;
  /** Ad-hoc scope items whose H2 heading (label) was renamed - fieldKey stays stable. */
  labelEdits: Array<{ fieldKey: string; label: string }>;
  businessProcessMoves: Array<{ fieldKey: string; businessProcess: string }>;
  newItems: Array<{
    fieldKey: string;
    label: string;
    text?: string | null;
    businessProcess?: string | null;
  }>;
  order?: string[];
  /**
   * User-authored note sections (generic H1 + body typed in free space) - full replace, present
   * only when they changed. Persisted doc-only (deal_scope_doc), never into the questionnaire.
   */
  userSections?: Array<{ key: string; blocks: Array<Record<string, unknown>> }>;
}

export interface ScopeDocHarvest {
  payload: ScopeDocumentPayload;
  hasChanges: boolean;
  /**
   * User-typed content the harvest cannot save (text outside a question, under a structured
   * question, or a heading typed in a template section). Non-empty = the caller should refuse
   * the save and tell the user, never silently drop it.
   */
  discarded: string[];
}

// ── Doc parsing ──────────────────────────────────────────────────────────────

type TiptapNode = { type?: unknown; attrs?: Record<string, unknown>; content?: unknown[] } & Record<
  string,
  unknown
>;

interface ParsedItem {
  /** The item heading text (its label) - editable H2, harvested into an updateLabel. */
  label: string;
  text: string[];
  briefText: Map<string, string>;
  briefTags: Map<string, string[]>;
  sectionKey: string | null;
}

interface ParsedNewItem {
  label: string;
  text: string[];
  sectionKey: string | null;
}

/** A user note section: raw blocks verbatim; key null until first save stamps one. */
interface ParsedUserSection {
  key: string | null;
  blocks: TiptapNode[];
}

interface ParsedDoc {
  /** Field bodies by fieldKey (every anchored question, text-control or not - diffed later). */
  fields: Map<string, string[]>;
  /** Scope items, by primary fieldKey. */
  items: Map<string, ParsedItem>;
  newItems: ParsedNewItem[];
  /** Primary item order: adhoc fieldKeys and '$new:<i>' refs, in doc order. */
  order: string[];
  /** Ad-hoc section labels by sectionKey (BP name; catch-all maps to ''). */
  sectionBp: Map<string, string>;
  /** User-authored note sections (generic H1 + body), in doc order. */
  userSections: ParsedUserSection[];
  /** Text found where nothing can be saved (no anchor / rejected new heading). */
  strays: string[];
}

const BRIEF_BY_LABEL = new Map<string, (typeof SCOPE_BRIEF)[number]>(
  SCOPE_BRIEF.map((d) => [d.label, d])
);
const BRIEF_BY_KEY = new Map<string, (typeof SCOPE_BRIEF)[number]>(
  SCOPE_BRIEF.map((d) => [d.key, d])
);

function isNode(v: unknown): v is TiptapNode {
  return !!v && typeof v === 'object';
}

const INLINE_TYPES = new Set(['text', 'scopeChip', 'hardBreak']);

/** Plain text of a node: inline runs concatenate, block children join with newlines. */
function nodeText(node: TiptapNode): string {
  if (node.type === 'text') return String((node as { text?: unknown }).text ?? '');
  const children = Array.isArray(node.content) ? (node.content.filter(isNode) as TiptapNode[]) : [];
  const parts = children.map(nodeText).filter((s) => s.trim().length > 0);
  const hasBlockChildren = children.some((c) => !INLINE_TYPES.has(String(c.type)));
  return parts.join(hasBlockChildren ? '\n' : '').trim();
}

function isBold(run: TiptapNode): boolean {
  const marks = (run as { marks?: Array<{ type?: unknown }> }).marks;
  return Array.isArray(marks) && marks.some((m) => m?.type === 'bold');
}

/**
 * Transient slash-menu trigger text ("/", "/bullet", ...) - the editor's menu trigger pattern,
 * momentarily in the doc while the menu is open, and LEFT BEHIND when it's dismissed with Escape.
 * Never content: without this guard the 1s autosave catches it and either refuses the save
 * (stray "/" -> "Some text cannot be saved...") or pollutes an answer/label with "/bullet".
 * Only WHOLE-block matches are ignored; "/" inside real text is untouched.
 */
function isSlashRemnant(text: string): boolean {
  return /^\/\w*$/.test(text.trim());
}

function rowDataKey(p: TiptapNode): string | null {
  const raw = p.attrs?.dataKey;
  return typeof raw === 'string' && raw ? raw : null;
}

/** Strip a leading "<label>: " brief prefix (the bold label, possibly merged with the value). */
function stripBriefPrefix(text: string, dimLabel: string): string {
  const prefix = `${dimLabel}:`;
  return text.startsWith(prefix) ? text.slice(prefix.length).trim() : text.trim();
}

/** Chip labels + comma-split plain text of a tag row (label prefix removed). */
function tagRowValues(p: TiptapNode, dimLabel: string): string[] {
  const tags: string[] = [];
  const content = Array.isArray(p.content) ? (p.content as TiptapNode[]) : [];
  content.forEach((c, i) => {
    if (c.type === 'scopeChip') {
      const label = String((c.attrs as { label?: unknown } | undefined)?.label ?? '').trim();
      if (label) tags.push(label);
      return;
    }
    if (c.type === 'text') {
      const raw = String((c as { text?: unknown }).text ?? '');
      const text = i === 0 ? stripBriefPrefix(raw, dimLabel) : raw;
      for (const t of text.split(',')) {
        const tt = t.trim();
        if (tt) tags.push(tt);
      }
    }
  });
  return tags;
}

/** Classify a paragraph inside a scope item's body. */
function classifyItemParagraph(
  p: TiptapNode
):
  | { kind: 'chips-row' }
  | { kind: 'brief-text'; dim: string; text: string }
  | { kind: 'brief-tags'; dim: string; tags: string[] }
  | { kind: 'text'; text: string } {
  // Anchor-first: builder-stamped rows keep their meaning even with a mangled label.
  // Any chips:* row is display-only (status/work-type/hours, enum answers, transcript
  // provenance) - never harvested.
  const rowKey = rowDataKey(p);
  if (rowKey?.startsWith('chips:')) return { kind: 'chips-row' };
  if (rowKey?.startsWith('brief:')) {
    const dim = BRIEF_BY_KEY.get(rowKey.slice('brief:'.length));
    if (dim) {
      if (dim.control === 'text') {
        return { kind: 'brief-text', dim: dim.key, text: stripBriefPrefix(nodeText(p), dim.label) };
      }
      return { kind: 'brief-tags', dim: dim.key, tags: tagRowValues(p, dim.label) };
    }
  }
  // Label-prefix fallback for unstamped rows. The label match tolerates the value having merged
  // into the SAME bold run (typing at the end of a bold label inherits the mark), so filling an
  // empty brief row classifies correctly.
  const content = Array.isArray(p.content) ? (p.content as TiptapNode[]) : [];
  const first = content[0];
  if (first && first.type === 'text' && isBold(first)) {
    const firstText = String((first as { text?: unknown }).text ?? '');
    const m = /^([^:]+):\s?/.exec(firstText);
    const dim = m ? BRIEF_BY_LABEL.get(m[1]!.trim()) : undefined;
    if (dim) {
      if (dim.control === 'text') {
        return { kind: 'brief-text', dim: dim.key, text: stripBriefPrefix(nodeText(p), dim.label) };
      }
      return { kind: 'brief-tags', dim: dim.key, tags: tagRowValues(p, dim.label) };
    }
  }
  return { kind: 'text', text: nodeText(p) };
}

export function parseScopeDoc(doc: Record<string, unknown>): ParsedDoc {
  const parsed: ParsedDoc = {
    fields: new Map(),
    items: new Map(),
    newItems: [],
    order: [],
    sectionBp: new Map(),
    userSections: [],
    strays: [],
  };
  const content = Array.isArray((doc as TiptapNode).content)
    ? ((doc as TiptapNode).content as TiptapNode[])
    : [];

  let sectionKey: string | null = null;
  let sectionIsAdhoc = false;
  type Cursor =
    | { kind: 'field'; key: string }
    | { kind: 'item'; item: ParsedItem }
    | { kind: 'new-item'; item: ParsedNewItem }
    | { kind: 'user'; us: ParsedUserSection }
    | null;
  let cursor: Cursor = null;
  // Trailing plain paragraphs after a brief TEXT row continue that dim (visual grouping wins).
  let lastBriefTextDim: string | null = null;

  for (const node of content) {
    if (!isNode(node)) continue;
    if (node.type === 'heading') {
      lastBriefTextDim = null;
      const anchor = parseScopeDocKey((node.attrs as { dataKey?: unknown } | undefined)?.dataKey);
      const text = nodeText(node);
      if (anchor?.type === 'section') {
        sectionKey = anchor.key;
        sectionIsAdhoc = isAdhocSectionKey(anchor.key);
        if (sectionIsAdhoc) {
          parsed.sectionBp.set(anchor.key, anchor.key === ADHOC_SECTION_KEY ? '' : text);
        }
        cursor = null;
        continue;
      }
      if (anchor?.type === 'adhoc') {
        const item: ParsedItem = {
          label: text,
          text: [],
          briefText: new Map(),
          briefTags: new Map(),
          sectionKey,
        };
        parsed.items.set(anchor.key, item);
        parsed.order.push(anchor.key);
        cursor = { kind: 'item', item };
        continue;
      }
      if (anchor?.type === 'field') {
        parsed.fields.set(anchor.key, []);
        cursor = { kind: 'field', key: anchor.key };
        continue;
      }
      if (anchor?.type === 'group') {
        cursor = null;
        continue;
      }
      // A round-tripped user note section (H1 stamped `user:<key>` by the builder): collect the
      // heading + its body verbatim. A user H1 ENDS any enclosing model zone.
      if (anchor?.type === 'user') {
        const us: ParsedUserSection = { key: anchor.key, blocks: [node] };
        parsed.userSections.push(us);
        sectionKey = null;
        sectionIsAdhoc = false;
        cursor = { kind: 'user', us };
        continue;
      }
      // dataKey-null heading, in priority order:
      // 1. A generic H1 anywhere = a NEW user note section (doc-only content; persisted, never
      //    captured into the questionnaire) - it ends any enclosing model zone.
      // 2. Inside a user section, any generic heading is note content (never a new item).
      // 3. Inside an ad-hoc section, an H2/H3 is a NEW scope item (the /problem path).
      // 4. Anything else cannot be saved - track as a stray, don't drop silently.
      // Slash-menu residue ("/", "/problem") is treated as EMPTY text: never a section/item/stray.
      const level = Number((node.attrs as { level?: unknown } | undefined)?.level ?? 0);
      const headingText = isSlashRemnant(text) ? '' : text;
      if (level === 1 && headingText) {
        const us: ParsedUserSection = { key: null, blocks: [node] };
        parsed.userSections.push(us);
        sectionKey = null;
        sectionIsAdhoc = false;
        cursor = { kind: 'user', us };
      } else if (cursor?.kind === 'user') {
        cursor.us.blocks.push(node);
      } else if (sectionIsAdhoc && (level === 2 || level === 3) && headingText) {
        const item: ParsedNewItem = { label: headingText, text: [], sectionKey };
        parsed.newItems.push(item);
        parsed.order.push(`$new:${parsed.newItems.length - 1}`);
        cursor = { kind: 'new-item', item };
      } else {
        if (headingText) parsed.strays.push(headingText);
        cursor = null;
      }
      continue;
    }

    // User note sections keep EVERY block verbatim (tables included) - push before the table skip.
    if (cursor?.kind === 'user') {
      cursor.us.blocks.push(node);
      continue;
    }

    if (node.type === 'table') continue; // tables are not harvested in this increment

    if (!cursor) {
      const stray = nodeText(node);
      if (stray && stray !== '-' && !isSlashRemnant(stray)) parsed.strays.push(stray);
      continue;
    }

    if (cursor.kind === 'field') {
      if (node.type === 'paragraph' && rowDataKey(node)?.startsWith('chips:')) continue;
      const t = nodeText(node);
      if (t && !isSlashRemnant(t)) parsed.fields.get(cursor.key)?.push(t);
      continue;
    }

    if (node.type === 'paragraph' && cursor.kind === 'item') {
      const cls = classifyItemParagraph(node);
      if (cls.kind === 'chips-row') {
        lastBriefTextDim = null;
        continue;
      }
      if (cls.kind === 'brief-text') {
        const prev = cursor.item.briefText.get(cls.dim);
        cursor.item.briefText.set(cls.dim, prev && cls.text ? `${prev}\n${cls.text}` : cls.text || (prev ?? ''));
        lastBriefTextDim = cls.dim;
        continue;
      }
      if (cls.kind === 'brief-tags') {
        const prev = cursor.item.briefTags.get(cls.dim) ?? [];
        cursor.item.briefTags.set(cls.dim, [...prev, ...cls.tags]);
        lastBriefTextDim = null;
        continue;
      }
      if (cls.text && cls.text !== '-' && !isSlashRemnant(cls.text)) {
        if (lastBriefTextDim) {
          const prev = cursor.item.briefText.get(lastBriefTextDim) ?? '';
          cursor.item.briefText.set(lastBriefTextDim, prev ? `${prev}\n${cls.text}` : cls.text);
        } else {
          cursor.item.text.push(cls.text);
        }
      }
      continue;
    }

    const t = nodeText(node);
    if (t && t !== '-' && !isSlashRemnant(t)) {
      (cursor.kind === 'item' ? cursor.item.text : cursor.item.text).push(t);
    }
  }
  return parsed;
}

// ── Diff ─────────────────────────────────────────────────────────────────────

function joined(lines: string[] | undefined): string {
  return (lines ?? []).join('\n').trim();
}

/** Parse an edited display text back into an answer value, per control. */
function parseBackValue(control: string, text: string): unknown {
  const t = text.trim();
  if (!t) return null;
  if (control === 'number') {
    const n = parseFloat(t.replace(/,/g, ''));
    return Number.isFinite(n) ? n : t;
  }
  if (control === 'currency') {
    // The shared coercion built for human-typed currency ("$85M", "60,000 USD"); raw text when
    // nothing parseable so the server still stores what the user wrote.
    return parseHumanCurrency(t) ?? t;
  }
  if (control === 'date') {
    // A typed ISO date passes through VERBATIM: new Date('yyyy-mm-dd') parses UTC, so a local
    // read-back (field-display-value appends T00:00:00) would show the previous day.
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    const d = new Date(t);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    return t;
  }
  return t;
}

function genFieldKey(label: string): string {
  const slug =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'question';
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${slug}_${rand}`;
}

function mapsEqual(a: Map<string, string>, b: Map<string, string>): boolean {
  const keys = new Set([...a.keys(), ...b.keys()]);
  for (const k of keys) if ((a.get(k) ?? '') !== (b.get(k) ?? '')) return false;
  return true;
}

export function harvestScopeDoc(args: {
  doc: Record<string, unknown>;
  questionnaire: SalesDealQuestionnaireSummary;
  answers: QuestionnaireAnswerMap;
  fieldLookup?: QuestionnaireFieldLookup;
  /**
   * The model AS THE USER SAW IT when the edit session began (snapshotted on first keystroke).
   * The diff baseline is projected from THIS, so concurrent changes to untouched fields are
   * never attributed to the user and reverted. Falls back to the current model.
   */
  baseline?: { questionnaire: SalesDealQuestionnaireSummary; answers: QuestionnaireAnswerMap };
}): ScopeDocHarvest {
  const base = args.baseline ?? { questionnaire: args.questionnaire, answers: args.answers };
  const baselineDoc = buildScopeDoc({
    questionnaire: base.questionnaire,
    answers: base.answers,
    fieldLookup: args.fieldLookup,
    mode: 'edit',
    // Baseline must include the persisted user sections, or an untouched doc would diff as a
    // whole-blob change on every run.
    userSections: base.questionnaire.userSections,
  });
  const baseline: ParsedDoc = baselineDoc
    ? parseScopeDoc(baselineDoc)
    : {
        fields: new Map(),
        items: new Map(),
        newItems: [],
        order: [],
        sectionBp: new Map(),
        userSections: [],
        strays: [],
      };
  const edited = parseScopeDoc(args.doc);
  const discarded: string[] = [];

  // Model index from the CURRENT questionnaire (templateFieldId / control / preserved workType).
  const modelFields = new Map<string, SalesDealTemplateFieldSummary>();
  for (const section of args.questionnaire.sections) {
    for (const group of section.groups) {
      for (const field of group.fields) modelFields.set(field.fieldKey, field);
    }
  }

  const payload: ScopeDocumentPayload = {
    answers: [],
    briefEdits: [],
    labelEdits: [],
    businessProcessMoves: [],
    newItems: [],
  };

  // Template / supplementary field text edits. Non-text controls are display-only: an edit
  // there cannot be saved - report it instead of silently dropping it.
  for (const [key, lines] of edited.fields) {
    const model = modelFields.get(key);
    if (!model) continue;
    const before = joined(baseline.fields.get(key));
    const after = joined(lines);
    if (before === after) continue;
    if (!SCOPE_DOC_TEXT_CONTROLS.has(model.uiControl)) {
      if (after) discarded.push(`${model.label || key}: ${after}`);
      continue;
    }
    payload.answers.push({
      templateFieldId: model.templateFieldId,
      fieldKey: key,
      value: parseBackValue(model.uiControl, after),
    });
  }

  // Scope item edits: discussion text, brief rows, BP moves.
  for (const [key, item] of edited.items) {
    const model = modelFields.get(key);
    const base2 = baseline.items.get(key);
    if (!model || !base2) continue;

    // Item heading (label) rename - fieldKey is the stable anchor, so answers/brief stay attached.
    const editedLabel = item.label.replace(/\s+/g, ' ').trim();
    if (editedLabel && editedLabel !== base2.label.trim()) {
      payload.labelEdits.push({ fieldKey: key, label: editedLabel });
    }

    const beforeText = joined(base2.text);
    const afterText = joined(item.text);
    if (beforeText !== afterText) {
      payload.answers.push({ templateFieldId: null, fieldKey: key, value: afterText || null });
    }

    if (!mapsEqual(base2.briefText, item.briefText)) {
      // Full brief-subset replace of the TEXT dims. workType AND the TAG dims (in/out scope,
      // complexity, assumptions) are edited via the live property controls, not the doc, so they're
      // PRESERVED from the current model here - otherwise this replace would drop them. A text dim the
      // user cleared in the doc is absent and drops server-side (updateBriefScopeMeta).
      const modelMeta = model.scopeMeta as Record<string, unknown> | null | undefined;
      const scopeMeta: Record<string, unknown> = {};
      if (modelMeta?.workType) scopeMeta.workType = modelMeta.workType;
      for (const dim of SCOPE_BRIEF) {
        if (dim.control === 'text') {
          const t = (item.briefText.get(dim.key) ?? '').trim();
          if (t) scopeMeta[dim.key] = t;
        } else {
          const tags = modelMeta?.[dim.key];
          if (Array.isArray(tags) && tags.length > 0) scopeMeta[dim.key] = tags;
        }
      }
      payload.briefEdits.push({ fieldKey: key, scopeMeta: scopeMeta as ScopeMeta });
    }

    if (item.sectionKey && base2.sectionKey && item.sectionKey !== base2.sectionKey) {
      const bp = edited.sectionBp.get(item.sectionKey);
      if (bp !== undefined) {
        payload.businessProcessMoves.push({ fieldKey: key, businessProcess: bp });
      }
    }
  }

  // Functional-area (Business-Process) section RENAME: a section heading whose text changed but whose
  // items stayed put isn't caught by the per-item move above (the item's sectionKey anchor is stable).
  // Re-tag every item still in a renamed group section to the new BP name (dedupe against moves).
  const alreadyMoved = new Set(payload.businessProcessMoves.map((m) => m.fieldKey));
  for (const [sectionKey, editedBp] of edited.sectionBp) {
    if (!isAdhocGroupSectionKey(sectionKey)) continue;
    const baseBp = baseline.sectionBp.get(sectionKey);
    if (baseBp === undefined || baseBp === editedBp) continue;
    for (const [itemKey, item] of edited.items) {
      if (item.sectionKey === sectionKey && !alreadyMoved.has(itemKey)) {
        payload.businessProcessMoves.push({ fieldKey: itemKey, businessProcess: editedBp });
        alreadyMoved.add(itemKey);
      }
    }
  }

  // New items typed into the doc.
  for (const it of edited.newItems) {
    const bp = it.sectionKey ? (edited.sectionBp.get(it.sectionKey) ?? null) : null;
    payload.newItems.push({
      fieldKey: genFieldKey(it.label),
      label: it.label,
      text: joined(it.text) || null,
      businessProcess: bp,
    });
  }

  // Text typed where nothing can be saved (the baseline never emits strays, so any delta is
  // user-typed).
  const baseStrays = new Set(baseline.strays);
  for (const s of edited.strays) if (!baseStrays.has(s)) discarded.push(s);

  // Item order (only when it changed or new items exist - the server reconciles leniently).
  const orderChanged =
    edited.order.length !== baseline.order.length ||
    edited.order.some((k, i) => baseline.order[i] !== k);
  if (orderChanged || payload.newItems.length > 0) payload.order = edited.order;

  // User note sections: full replace when the blob changed (JSON compare against the baseline -
  // blocks originate from getJSON() and the builder re-emits them verbatim, so an untouched doc
  // compares equal). New sections (key null) get a stable generated key, stamped into the payload
  // copy of the H1's dataKey so the stored form equals what the builder re-emits next load.
  if (
    JSON.stringify(edited.userSections) !== JSON.stringify(baseline.userSections)
  ) {
    payload.userSections = edited.userSections.map((us) => {
      const key = us.key ?? genFieldKey(nodeText(us.blocks[0] ?? {}) || 'notes');
      const blocks = us.blocks.map((b, i) =>
        i === 0 && b.type === 'heading'
          ? { ...b, attrs: { ...(b.attrs ?? {}), dataKey: `user:${key}` } }
          : b
      );
      return { key, blocks: blocks as Array<Record<string, unknown>> };
    });
  }

  const hasChanges =
    payload.answers.length > 0 ||
    payload.briefEdits.length > 0 ||
    payload.labelEdits.length > 0 ||
    payload.businessProcessMoves.length > 0 ||
    payload.newItems.length > 0 ||
    payload.order !== undefined ||
    payload.userSections !== undefined;
  return { payload, hasChanges, discarded };
}
